import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Loader2, Truck } from "lucide-react";
import React, { useState } from "react";

import {
  fetchJournalPhotoUrl,
  getPalletScans,
  type PalletLine,
  type PalletView,
  type ScanView,
} from "@/api/order_journal";
import { Card } from "@/components/ui/card";
import { fmtDateTime } from "@/lib/format";
import {
  LEGACY_TEXT,
  authorsText,
  fmtQty,
  isManualSource,
  lineTitle,
  NO_UNIT_HINT,
  palletNumberText,
  palletStatus,
  palletTitle,
  qtySourceTitle,
  scanKindTitle,
  shipmentText,
} from "@/lib/order-journal";
import { apiErrorText } from "@/lib/scan-ui/screen";
import { cn } from "@/lib/utils";
import { LazyThumb, StatusBadge } from "@/pages/order-journal/parts";

/** Сколько превью показывать в карточке; остальные — в галерее. */
const THUMBS_IN_CARD = 6;

/**
 * Карточка паллеты журнала: SSCC (и номер 1С импортированной) и подпись, статус, превью фото,
 * отгрузка, состав агрегатом (ручной ввод выделен), первый/последний скан и
 * авторы. Сырые сканы — по раскрытию, отдельным запросом: на 60 паллет их
 * тысячи, и тянуть их все ради открытия страницы незачем.
 */
export function PalletCard({
  orderRef,
  pallet,
  filtered,
  showSession,
  focused,
  onOpenPhoto,
}: {
  orderRef: string;
  pallet: PalletView;
  /** Применён фильтр по товару — подсветить совпавшие строки. */
  filtered: boolean;
  /** У заказа несколько проверок — подписать, из какой паллета. */
  showSession: boolean;
  /** Паллета найдена поиском по коду — выделить рамкой. */
  focused: boolean;
  onOpenPhoto: (pallet: PalletView, index: number) => void;
}): React.ReactElement {
  const [scansOpen, setScansOpen] = useState(false);
  const st = palletStatus(pallet.status);
  const legacy = pallet.lines.reduce((n, l) => n + l.legacy_scans, 0);
  const matched = pallet.lines.filter((l) => l.matches);
  const matchedQty = matched.reduce((n, l) => n + l.qty_base, 0);
  const matchedScans = matched.reduce((n, l) => n + l.scans, 0);
  const title = palletTitle(palletNumberText(pallet), pallet.label);

  return (
    <Card
      id={`pallet-${pallet.id}`}
      data-testid="journal-pallet"
      className={cn(
        "space-y-3 p-3 [contain-intrinsic-size:auto_360px] [content-visibility:auto] sm:p-4",
        focused && "ring-2 ring-sky-500",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <h3 className="text-base font-semibold">{title}</h3>
        <StatusBadge tone={st.tone}>{st.text}</StatusBadge>
        {pallet.boxes !== null && pallet.boxes > 0 && (
          <span className="text-xs text-muted-foreground">коробов {pallet.boxes}</span>
        )}
        {pallet.is_planned && <StatusBadge tone="muted">по плану из 1С</StatusBadge>}
        {showSession && pallet.session_display_number !== null && (
          <span className="text-xs text-muted-foreground">
            проверка № {pallet.session_display_number}
          </span>
        )}
        <span className="ml-auto text-sm tabular-nums">
          {fmtQty(pallet.qty_base)}
          <span className="text-xs text-muted-foreground"> · сканов {pallet.scans}</span>
          {pallet.manual_scans > 0 && (
            <span className="ml-1 rounded bg-amber-100 px-1 text-xs text-amber-800">
              вручную {pallet.manual_scans}
            </span>
          )}
        </span>
      </div>

      {filtered && (
        <div className="rounded-md bg-sky-50 px-2 py-1 text-sm text-sky-900">
          Выбранного товара на паллете: <b className="tabular-nums">{fmtQty(matchedQty)}</b>
          <span className="text-xs"> · сканов {matchedScans}</span>
        </div>
      )}

      {!pallet.cargo_unit_id && (
        <div data-testid="journal-no-unit" className="rounded-md bg-red-50 px-2 py-1 text-sm text-red-900">
          Паллета без грузоместа — {NO_UNIT_HINT}.
        </div>
      )}

      <ShipmentLine pallet={pallet} />

      {pallet.photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {pallet.photos.slice(0, THUMBS_IN_CARD).map((ph, i) => (
            <LazyThumb
              key={ph.id}
              cacheKey={`thumb:${ph.id}`}
              load={() => fetchJournalPhotoUrl(orderRef, ph.id, true)}
              onClick={() => onOpenPhoto(pallet, i)}
              label={`Фото ${i + 1} паллеты ${title}`}
            />
          ))}
          {pallet.photos.length > THUMBS_IN_CARD && (
            <button
              type="button"
              onClick={() => onOpenPhoto(pallet, THUMBS_IN_CARD)}
              className="flex size-20 items-center justify-center rounded-md border text-sm text-muted-foreground hover:bg-accent sm:size-24"
            >
              ещё {pallet.photos.length - THUMBS_IN_CARD}
            </button>
          )}
        </div>
      )}
      {pallet.photos.length === 0 && (
        <p className="text-xs text-muted-foreground">Фото паллеты нет</p>
      )}

      <div className="grid gap-x-4 gap-y-1 text-xs text-muted-foreground sm:grid-cols-2">
        <span>
          Сборка: {pallet.first_scan_at ? fmtDateTime(pallet.first_scan_at) : "—"}
          {" — "}
          {pallet.last_scan_at ? fmtDateTime(pallet.last_scan_at) : "—"}
        </span>
        <span>Собирали: {authorsText(pallet.authors, legacy)}</span>
        {pallet.printed_at && <span>Этикетка напечатана {fmtDateTime(pallet.printed_at)}</span>}
      </div>

      {pallet.lines.length === 0 ? (
        <p className="text-sm text-muted-foreground">Паллета пустая — сканов нет.</p>
      ) : (
        <div className="divide-y rounded-md border">
          {pallet.lines.map((l) => (
            <LineRow
              key={`${l.nomenclature_ref}|${l.characteristic_ref}|${l.partia_ref}`}
              line={l}
              filtered={filtered}
            />
          ))}
        </div>
      )}

      {pallet.scans > 0 && (
        <button
          type="button"
          onClick={() => setScansOpen((v) => !v)}
          aria-expanded={scansOpen}
          className="flex items-center gap-1 text-sm font-medium text-sky-700"
        >
          {scansOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {scansOpen ? "Скрыть сканы" : `Показать сканы (${pallet.scans})`}
        </button>
      )}
      {scansOpen && <PalletScansList palletId={pallet.id} />}
    </Card>
  );
}

function ShipmentLine({ pallet }: { pallet: PalletView }): React.ReactElement {
  if (!pallet.shipment) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-dashed px-2 py-1 text-sm text-muted-foreground">
        <Truck className="size-4 shrink-0" /> Не отгружена
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-md bg-emerald-50 px-2 py-1 text-sm text-emerald-900">
      <Truck className="mt-0.5 size-4 shrink-0" />
      <span className="min-w-0 break-words">{shipmentText(pallet.shipment)}</span>
    </div>
  );
}

function LineRow({ line, filtered }: { line: PalletLine; filtered: boolean }): React.ReactElement {
  const allLegacy = line.legacy_scans > 0 && line.legacy_scans === line.scans;
  return (
    <div
      data-testid="journal-line"
      data-matches={filtered && line.matches ? "true" : undefined}
      className={cn(
        "flex flex-wrap items-start gap-x-3 gap-y-1 px-2 py-2 text-sm",
        filtered && line.matches && "border-l-4 border-l-sky-500 bg-sky-50",
        filtered && !line.matches && "opacity-60",
      )}
    >
      <div className="min-w-0 flex-1 basis-56">
        <div className="break-words">{lineTitle(line)}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-muted-foreground">
          <span>сканов {line.scans}</span>
          {allLegacy ? (
            <span>ручной ввод: {LEGACY_TEXT}</span>
          ) : line.manual_scans > 0 ? (
            <span className="rounded bg-amber-100 px-1 font-medium text-amber-800">
              из них вручную {line.manual_scans} ({fmtQty(line.manual_qty_base)})
            </span>
          ) : (
            <span>вручную 0</span>
          )}
          <span>
            {line.first_scan_at ? fmtDateTime(line.first_scan_at) : "—"}
            {" — "}
            {line.last_scan_at ? fmtDateTime(line.last_scan_at) : "—"}
          </span>
          <span>{authorsText(line.authors, line.legacy_scans)}</span>
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="font-semibold tabular-nums">{fmtQty(line.qty_base)}</div>
        {line.boxes > 0 && (
          <div className="text-xs text-muted-foreground">коробов {fmtQty(line.boxes)}</div>
        )}
      </div>
    </div>
  );
}

function PalletScansList({ palletId }: { palletId: string }): React.ReactElement {
  const q = useQuery({
    queryKey: ["order-journal-scans", palletId],
    queryFn: () => getPalletScans(palletId),
    staleTime: 60_000,
  });
  if (q.isLoading) {
    return (
      <div className="flex justify-center py-3 text-muted-foreground">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div role="alert" className="rounded-md border border-rose-300 bg-rose-50 p-2 text-sm text-rose-800">
        Сканы не загрузились: {apiErrorText(q.error)}{" "}
        <button type="button" className="underline" onClick={() => void q.refetch()}>
          Повторить
        </button>
      </div>
    );
  }
  const scans = q.data?.scans ?? [];
  if (scans.length === 0) {
    return <p className="text-sm text-muted-foreground">Сканов нет.</p>;
  }
  return (
    <div className="divide-y rounded-md border text-xs" data-testid="journal-scans">
      {scans.map((s) => (
        <ScanRow key={s.id} scan={s} />
      ))}
    </div>
  );
}

function ScanRow({ scan }: { scan: ScanView }): React.ReactElement {
  const manual = isManualSource(scan.qty_source);
  return (
    <div className={cn("flex flex-wrap gap-x-3 gap-y-0.5 px-2 py-1.5", manual && "bg-amber-50")}>
      <span className="tabular-nums text-muted-foreground">{fmtDateTime(scan.created_at)}</span>
      <span>{scan.scanned_by ?? LEGACY_TEXT}</span>
      <span className="text-muted-foreground">{scanKindTitle(scan.scan_kind)}</span>
      <span className="min-w-0 basis-full break-all font-mono sm:basis-auto">{scan.code || "—"}</span>
      <span className="min-w-0 basis-full break-words sm:basis-auto">{lineTitle({
        nomenclature_article: scan.nomenclature_article ?? "",
        nomenclature_name: scan.nomenclature_name ?? "",
        characteristic_name: scan.characteristic_name ?? "",
        partia_name: scan.partia_name ?? "",
      })}</span>
      <span className="ml-auto tabular-nums">
        {fmtQty(scan.qty_scanned)}
        {scan.unit_coefficient !== 1 && ` × ${fmtQty(scan.unit_coefficient)}`} ={" "}
        <b>{fmtQty(scan.qty_base)}</b>
      </span>
      <span className={cn(manual ? "font-medium text-amber-800" : "text-muted-foreground")}>
        {qtySourceTitle(scan.qty_source)}
      </span>
    </div>
  );
}
